
import './App.css';
import { Box, Container, Grid, Typography } from '@mui/material';
import LineChart from 'react-linechart';
import Spline from 'cubic-spline';
import Slider from '@mui/material/Slider';
import { useEffect, useState } from 'react';
import NativeSelect from '@mui/material/NativeSelect';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import ImportDialog from './components/ImportDialog';
import MotorSelector from './components/motorSelector';
import { set, without } from 'lodash';
import PhaseAmpField from './components/PhaseMapDialog';

export const RPM = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000, 8500, 9000]
export const MOTORS = ['KO RS', 'KO MX', 'SURRON'];
export const MOTORS_RATED_RPM = [6000, 4000, 6000];

function App() {
  const CUT_OFF_RPM = 9000;
  const [ys, setYs] = useState([95, 100, 85, 45]);
  const [xs, setXs] = useState([500, 1500, MOTORS_RATED_RPM[0], 9000]);
  const [maxPhaseAmp, setMaxPhaseAmp] = useState(800);
  const [motor, setMotor] = useState(MOTORS[0]);
  const [cutRPM, setCutRPM] = useState(CUT_OFF_RPM);
  const [points, setPoints] = useState([]);
  const [showImportScreen, setShowImportScreen] = useState(false);
  const [isImportedPoints, setIsImportedPoints] = useState(false);

  useEffect(() => { calcPoints(); }, []);
  useEffect(() => {
    if (!isImportedPoints) {
      calcPoints();
    }
  }, [xs, ys, cutRPM, isImportedPoints, motor]);

  const onImportSumbit = (points) => {
    setShowImportScreen(false);

    setPoints(points)
    // set the sliders
    let yPoints = [];
    xs.forEach(x => {
      const index = RPM.indexOf(x);
      yPoints.push(points[index].y)
    });

    setYs(yPoints);
    setIsImportedPoints(true);
  }

  const onAutoSmooth = () => {
    setIsImportedPoints(false)

  }

  const onMotorChange = (value) => {
    setMotor(value)
    setXs(set(xs, 2, value))
  }

  const calcPoints = () => {
    const points = []
    const spline = new Spline(xs, ys);

    for (let index = 0; index < RPM.length; index++) {
      const x = RPM[index];
      let y = Math.max(Math.min(spline.at(RPM[index]), 100), 5)
      if (x > cutRPM) { y = 5 }
      points.push({ x: x, y: y })
    }
    setPoints(points);
  };

  const data = [
    {
      color: "steelblue",
      points: points,
    }
  ];

  const pointPlot = () => {
    return (<Box>
      {
        points.map(point => {
          const value = Math.round(point.y)
          const phaseAMP = maxPhaseAmp / 100 * value;
          return (<Grid key={point.x} container spacing={2} alignItems="center">
            <Grid item><Typography>RPM: {point.x}</Typography></Grid>
            <Grid item><Typography>Value: <b>{value}</b></Typography></Grid>
            <Grid item><Typography variant="caption">(Phase AMP: {phaseAMP})</Typography></Grid>
          </Grid>)
        })
      }
    </Box>);
  };

  const wpBox = () => {
    return (
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel variant="standard" htmlFor="uncontrolled-native">
          Cut off RPM
        </InputLabel>
        <NativeSelect
          disabled={isImportedPoints}
          onChange={(event) => { setCutRPM(Number(event.target.value)); setXs(Object.assign([], xs, { 3: Number(event.target.value) })) }}
          defaultValue={CUT_OFF_RPM}
          inputProps={{
            name: 'rpm',
            id: 'uncontrolled-native',
          }}
        >
          {RPM.filter((value) => { return value > 3000 }).map(value => {
            return (<option key={value} value={value}>{value}</option>)
          })
          }
        </NativeSelect>
      </FormControl>
    );
  }

  return (
    <Container maxWidth="sm">
      <ImportDialog onClose={() => setShowImportScreen(false)} onSubmit={onImportSumbit} showImportScreen={showImportScreen} />
      <Box>
        <h1>eMoto tuner</h1>
        <p>This tuner allows you to easily set your controller's current to RPM ratio using four simple sliders. The turner will smooth the curve for a pleasant acceleration without dead spots or spikes of power.</p>
        <Box>
        </Box>
        <hr />
        <LineChart
          width={350}
          height={280}
          xLabel="Torque (current) / RPM"
          yLabel="Torque (current)"
          interpolate="linear"
          data={data}
          yMin={0}
          yMax={100}
          xMax={9000}
          xMin={500}
          ticks={6}
          hideYLabel
          pointRadius={2}
          margins={{ top: 50, right: 20, bottom: 50, left: 35 }}
        />

        {isImportedPoints ? <Button onClick={onAutoSmooth}>Auto Smooth</Button> : <Button onClick={() => setShowImportScreen(true)}>Import Curve</Button>}
        <hr />
        <Box sx={{ maxWidth: 400, paddingTop: 2 }}>

          <MotorSelector isImportedPoints={isImportedPoints} onChnage={(event) => { onMotorChange(Number(event.target.value)) }} />
          <PhaseAmpField onChange={(value) => { setMaxPhaseAmp(value) }} value={maxPhaseAmp} />
          <Box sx={{ paddingTop: 2 }}>
            <Typography gutterBottom>Stand still torque</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Slider disabled={isImportedPoints} value={ys[0]} onChange={(event, newValue) => { setYs(Object.assign([], ys, { 0: newValue })) }} />
              </Grid>
              <Grid item>
                <Typography>{ys[0]}%</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography gutterBottom>Acceleration torque</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Slider disabled={isImportedPoints} value={ys[1]} onChange={(event, newValue) => { setYs(Object.assign([], ys, { 1: newValue })) }} />
              </Grid>
              <Grid item>
                <Typography>{ys[1]}%</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography gutterBottom>Medium range torque</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Slider disabled={isImportedPoints} value={ys[2]} onChange={(event, newValue) => { setYs(Object.assign([], ys, { 2: newValue })) }} />
              </Grid>
              <Grid item>
                <Typography>{ys[2]}%</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography gutterBottom>Top speed (field weakening)</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Slider disabled={isImportedPoints} value={ys[3]} onChange={(event, newValue) => { setYs(Object.assign([], ys, { 3: newValue })) }} />
              </Grid>
              <Grid item>
                <Typography>{ys[3]}%</Typography>
              </Grid>
            </Grid>
          </Box>
          {wpBox()}
          <p>

            <Typography variant="caption">Note: field weakening will start above the rated motor RPM</Typography>
          </p>
        </Box>
      </Box>

      <hr />
      <Box sx={{ paddingBottom: 10 }}>
        <p>Copy the value of each RPM setting into your controller's software</p>
        {pointPlot()}
      </Box>

      <hr />
      <Box sx={{ paddingBottom: 5 }}>
        <Typography variant="caption">
          Made in Melbourne, Australia by Max.
        </Typography>
      </Box>

    </Container >
  );
}

export default App;
